import React from 'react'
import {
  Box,
  Divider,
  Rating,
  Stack,
  Typography,
  type SxProps,
} from '@mui/material'
import { Image, type ImageData } from '@media-components'
import { StartRating } from '@components/common'
import { commonStyles, CustomStyles, getStyles } from '@utils/styles'
import defaultStyles, { StyleClassNames } from './styles'

export type StatsCardData = {
  title: string
  subtitle?: string
  rating?: number
  iconData?: ImageData
  imageData?: ImageData
}

export type StatsCardProps = StatsCardData & {
  csx?: CustomStyles<StyleClassNames>
  isLast?: boolean
  wrapperSx?: SxProps
}

export function StatsCard({
  title,
  subtitle,
  rating,
  iconData,
  imageData,
  csx,
  isLast = false,
  wrapperSx,
}: StatsCardProps) {
  const styles = getStyles(defaultStyles, csx)

  const mobileViewRenderer = () => {
    return (
      <Stack
        flexDirection="row"
        gap={isLast ? 0 : 7.52}
        sx={{
          display: {
            xs: 'flex',
            md: 'none',
          },
          ...(iconData && {
            alignItems: 'center',
            justifyContent: 'center',
          }),
          ...wrapperSx,
        }}
      >
        <Box {...styles('wrapper')}>
          {iconData && rating ? (
            <StartRating
              title={title}
              subtitle={subtitle}
              rating={rating}
              iconData={iconData}
              csx={{
                iconContainer: styles('mobileIconContainer').sx,
                icon: styles('mobileIcon').sx,
              }}
            />
          ) : (
            <Box {...styles('content')}>
              <Stack spacing={0.5} alignItems="center">
                <Typography variant="body1.600" textAlign="center">
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="caption1.500"
                    color="text.secondary"
                    textAlign="center"
                  >
                    {subtitle}
                  </Typography>
                )}
                {iconData && <Image {...iconData} {...styles('mobileIcon')} />}
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    )
  }

  return (
    <>
      {mobileViewRenderer()}
      <Stack
        flexDirection="row"
        gap={isLast ? 0 : 7.52}
        display={{
          xs: 'none',
          md: 'flex',
        }}
      >
        <Box {...styles('wrapper')}>
          <Box {...styles('content')}>
            {iconData && <Image {...iconData} {...styles('icon')} />}

            <Stack
              spacing={0.5}
              alignItems={iconData ? 'flex-start' : 'center'}
            >
              <Typography variant="body1.600">{title}</Typography>
              <Box sx={commonStyles.flexRow(1)}>
                {rating && (
                  <Rating defaultValue={rating} readOnly precision={0.5} />
                )}
                {subtitle && (
                  <Typography variant="caption1.500" color="text.secondary">
                    {iconData && rating
                      ? `${rating}/5 | ${subtitle}`
                      : subtitle}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </Box>
        {!isLast && (
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              ...(iconData && {
                display: {
                  xs: 'none',
                  md: 'block',
                },
              }),
            }}
          />
        )}
      </Stack>
    </>
  )
}
